import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import SitePlannerViewer from '../Viewer/SitePlannerViewer';
import XlabsStageChrome from '../Xlabs/XlabsStageChrome';
import DesignToolRail from '../Xlabs/DesignToolRail';
import DesignSidePanel from '../Xlabs/DesignSidePanel';
import XlabsThemeViewPanel from '../Xlabs/XlabsThemeViewPanel';
import { useXlabsConfigurator } from '../../context/XlabsConfiguratorContext';
import useInternalTheme from '../../hooks/useInternalTheme';
import { catalogLabel } from '../../data/sitePlannerCatalog';
import { formatFt } from '../../utils/unitsUs';
import './XlabsLocate.css';

/**
 * xlabs Design hybrid — xlabs chrome + Construx Place (catalog on lot).
 * See docs/XLABS_DESIGN.md
 */
export default function XlabsDesign() {
  const state = useXlabsConfigurator();
  const theme = useInternalTheme();
  const localizeRef = useRef(null);

  const {
    tool,
    setTool,
    setLotSelected,
    setSelectedId,
    selectedId,
    removeStructure,
    addStructure,
    catalogType,
    structures,
    site,
    siteContentReady,
  } = state;

  const onToolChange = useCallback(
    (next) => {
      setTool(next);
      if (next === 'place') {
        setLotSelected(false);
      }
    },
    [setTool, setLotSelected],
  );

  const onAddStructure = useCallback(
    (type) => {
      addStructure(type);
      setTool('select');
    },
    [addStructure, setTool],
  );

  useEffect(() => {
    // Design defaults to select (not draw from Locate).
    if (tool === 'draw') setTool('select');
  }, [tool, setTool]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }
      const k = e.key.toLowerCase();
      if (k === 'v') onToolChange('select');
      else if (k === 'p') onToolChange('place');
      else if (k === 'm') onToolChange('measure');
      else if (k === 'escape') setSelectedId(null);
      else if (
        (k === 'backspace' || k === 'delete') &&
        selectedId
      ) {
        e.preventDefault();
        removeStructure(selectedId);
      } else if (k === 'enter' && tool === 'place') {
        e.preventDefault();
        if (site && siteContentReady) onAddStructure(catalogType);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    onToolChange,
    setSelectedId,
    selectedId,
    removeStructure,
    tool,
    onAddStructure,
    catalogType,
    site,
    siteContentReady,
  ]);

  const onBindLocalize = useCallback((fn) => {
    localizeRef.current = fn;
  }, []);

  const hudLeft = [
    { k: 'tool', v: tool },
    { k: 'catalog', v: catalogLabel(catalogType) },
    { k: 'placed', v: String(structures.length) },
  ];
  const hudRight = [
    {
      k: 'selected',
      v: selectedId
        ? catalogLabel(
            structures.find((s) => s.id === selectedId)?.type || catalogType,
          )
        : '—',
    },
    {
      k: 'floor',
      v:
        state.floorY != null && Number.isFinite(state.floorY)
          ? formatFt(state.floorY)
          : '—',
    },
  ];

  const layers = useMemo(
    () => ({ roof: true, walls: true, interior: true }),
    [],
  );
  const section = useMemo(() => ({ enabled: false, offset: 0 }), []);

  return (
    <div
      className="xlabs-stage"
      data-hub-theme={theme === 'light' ? undefined : theme}
    >
      <Helmet>
        <title>xlabs · design</title>
        <meta
          name="description"
          content="xlabs Design — place Construx catalog structures on the lot."
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <XlabsThemeViewPanel />

      <XlabsStageChrome
        activeStage="design"
        tool={tool}
        ToolRail={
          <DesignToolRail tool={tool} onToolChange={onToolChange} />
        }
        SidePanel={
          <DesignSidePanel
            site={state.site}
            floorY={state.floorY}
            siteContentReady={state.siteContentReady}
            catalogType={catalogType}
            onCatalogTypeChange={state.setCatalogType}
            onAddStructure={onAddStructure}
            structures={structures}
            selectedId={selectedId}
            onSelectStructure={setSelectedId}
            onRemoveStructure={removeStructure}
            tool={tool}
          />
        }
        siteBooting={state.siteBooting}
        siteLoading={state.siteLoading}
        floorY={state.floorY}
        site={state.site}
        hudLeft={hudLeft}
        hudRight={hudRight}
        northDeg={state.northDeg}
        showAddressSearch={false}
        bootName="xlabs · design"
        statusHint="tap Models to pick a catalog ADU"
        modelLoadLabel={state.modelLoadLabel}
        sideProps={{}}
        panelToggleLabel="Models"
        autoOpenPanel
        openPanelOnTool="place"
        compactPanelCta="Models · Add"
        closePanelOnAdd
        closePanelOnSelect
      >
        <div className="locate-viewer">
          <SitePlannerViewer
            site={state.site}
            lot={state.lot}
            onLotChange={state.onLotChange}
            mapBuildings={state.mapBuildings}
            contextMode={state.contextMode}
            tileSnapshotLevel={state.tileSnapshotLevel}
            onTilesAttemptFailed={state.onTilesAttemptFailed}
            viewLocked={state.viewLocked}
            onViewLockedChange={state.setViewLocked}
            lotSelected={false}
            onLotSelectedChange={() => {}}
            floorY={state.floorY}
            onFloorLock={state.onFloorLock}
            onFloorDebug={state.onFloorDebug}
            floorDebug={state.floorDebug}
            structures={structures}
            selectedId={selectedId}
            onSelectStructure={setSelectedId}
            onUpdateStructure={state.updateStructure}
            onStructureLoadChange={state.onStructureLoadChange}
            layers={layers}
            section={section}
            onBindLocalize={onBindLocalize}
            setbackMeters={state.setback}
            northDeg={state.northDeg}
            className="!h-full"
            heightClass="h-full"
          />
        </div>
      </XlabsStageChrome>
    </div>
  );
}
